// ── Automations flow editor ───────────────────────────────────────
// The editable infinite canvas: a palette on the left, the flow in the
// middle, an inspector on the right. Drag a step in, drag the dots to
// connect (one output per step; if/then is the only branch — yes/no),
// drop a step onto a wire to splice it in, edit in the inspector,
// undo/redo (⌘Z / ⌘⇧Z), and AUTO-SAVE on every change.
//
// Ported from the approved prototype. Reuses the au-* CSS + CRM 2.0
// tokens; saves the whole graph via automation-flows-update (which
// re-validates it against the owned-channels compliance gate).

import { upgradeSelects } from './utils.js';

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// One inspector field (label + control + optional help). Module-level so
// the per-trigger settings renderer and renderInspector share it.
const F = (label, html, help) => `<div class="aue-field"><label>${esc(label)}</label>${html}${help ? `<div class="aue-help">${esc(help)}</div>` : ''}</div>`;

const IC = {
  bolt: 'M13 2 3 14h9l-1 8 10-12h-9z', clock: 'M12 6v6l4 2M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
  mail: 'M2 4h20v16H2zM22 6l-10 7L2 6', bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  chat: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', task: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  branch: 'M6 3v12a3 3 0 0 0 3 3h9M14 14l4 4-4 4', target: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  tag: 'M20.6 13.4 12 22l-9-9V3h10zM7.5 7.5h.01', user: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  check: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11', edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
  cal: 'M3 4h18v18H3zM16 2v4M8 2v4M3 10h18', note: 'M4 4h16v12l-4 4H4zM14 20v-4h4',
  lock: 'M5 11h14v11H5zM8 11V7a4 4 0 0 1 8 0v4',
  slack: 'M6 15a2 2 0 1 1-2-2h2zM9 15v4a2 2 0 1 1-4 0 2 2 0 0 1 2-2h2zM9 6a2 2 0 1 1 2 2H9zM9 9H5a2 2 0 1 1 0-4 2 2 0 0 1 2 2z',
  sheet: 'M3 3h18v18H3zM3 9h18M9 21V9', notion: 'M4 4h13l3 3v13H4zM8 8h8M8 12h8M8 16h5', webhook: 'M12 8a4 4 0 0 0-4 4M12 8a4 4 0 0 1 4 4M12 8V4M6 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  dumbbell: 'M2 12h20M5 8v8M8 6v12M16 6v12M19 8v8', trophy: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0zM7 4H5a2 2 0 0 0 2 3M17 4h2a2 2 0 0 1-2 3',
  flame: 'M12 2c1 3 4 4 4 8a4 4 0 0 1-8 0c0-2 1-3 2-4 0 2 2 2 2 0 0-2 0-3 0-4z', apple: 'M12 7c-2-2-6-1-6 3s2 8 4 8 1-1 2-1 2 1 3 1 4-4 4-8-4-5-6-3zM12 7V4a2 2 0 0 1 2-2',
  camera: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l2-3h8l2 3h3a2 2 0 0 1 2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  gift: 'M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H8a2 2 0 1 1 2-4c2 0 2 4 2 4zM12 7h4a2 2 0 1 0-2-4c-2 0-2 4-2 4z', layers: 'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  repeat: 'M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3', doc: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h4',
  heart: 'M12 21s-7-4.5-9.5-8A5 5 0 0 1 12 6a5 5 0 0 1 9.5 7C19 16.5 12 21 12 21z',
  undo: 'M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-4', redo: 'M15 14l5-5-5-5M20 9H9a5 5 0 0 0 0 10h4',
};
const svgIcon = (p) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${p}"/></svg>`;

// Palette catalog — kept in sync with the backend allowlist. New types
// coaches drag in are re-validated server-side on save.
// Short, scannable group names (à la GHL: one or two words). `kind`
// splits the palette into its two halves — a "When it happens" (triggers)
// header and a "Then do this" (actions) header render above the groups.
const CATALOG = [
  // ── triggers ──────────────────────────────────────────────────────
  { group: 'Training', kind: 'trigger', cat: 'trigger', items: [
    { type: 'trigger.workout_logged', name: 'Logged a workout', ic: 'dumbbell', sub: 'Any completed session' },
    { type: 'trigger.pr', name: 'Hit a new PR', ic: 'trophy', sub: 'Personal record on a lift' },
    { type: 'trigger.streak', name: 'Hit a streak', ic: 'flame', sub: 'Streak reaches N days' },
  ] },
  { group: 'Nutrition', kind: 'trigger', cat: 'trigger', items: [
    { type: 'trigger.nutrition_hit', name: 'Hit nutrition targets', ic: 'apple', sub: 'Macros on point' },
    { type: 'trigger.nutrition_missed', name: 'Missed nutrition targets', ic: 'apple', sub: 'Off target N days' },
    { type: 'trigger.nutrition_logged', name: 'Logged nutrition', ic: 'apple', sub: 'Any food log' },
  ] },
  { group: 'Progress', kind: 'trigger', cat: 'trigger', items: [
    { type: 'trigger.checkin', name: 'Check-in submitted', ic: 'check', sub: 'Weekly check-in lands' },
    { type: 'trigger.milestone_unlocked', name: 'Hits a milestone', ic: 'trophy', sub: 'Any catalog milestone' },
    { type: 'trigger.progress_photo', name: 'Progress photo added', ic: 'camera', sub: 'New photo uploaded' },
    { type: 'trigger.goal_weight', name: 'Weight goal reached', ic: 'target', sub: 'Hits target weight' },
  ] },
  { group: 'Engagement', kind: 'trigger', cat: 'trigger', items: [
    { type: 'trigger.client_message', name: 'Client replies', ic: 'chat', sub: 'Inbound message' },
    { type: 'trigger.session_missed', name: 'Session missed', ic: 'clock', sub: 'Credit lost or cancelled' },
  ] },
  { group: 'Clients', kind: 'trigger', cat: 'trigger', items: [
    { type: 'trigger.new_client', name: 'New client added', ic: 'user', sub: 'Onboarding starts' },
    { type: 'trigger.tag_added', name: 'Tag added', ic: 'tag', sub: 'You tag the client' },
    { type: 'trigger.tenure', name: 'Client for X time', ic: 'heart', sub: 'Tenure / anniversary' },
    { type: 'trigger.birthday', name: 'Birthday', ic: 'gift', sub: 'On their day' },
  ] },
  { group: 'Billing', kind: 'trigger', cat: 'trigger', items: [
    { type: 'trigger.payment_failed', name: 'Payment fails', ic: 'bolt', sub: 'Any recurring charge' },
    { type: 'trigger.agreement_ending', name: 'Agreement ending', ic: 'cal', sub: 'Renewal window opens' },
    { type: 'trigger.agreement_signed', name: 'Agreement signed', ic: 'check', sub: 'Fully executed' },
  ] },
  { group: 'External', kind: 'trigger', cat: 'trigger', items: [
    { type: 'trigger.webhook_in', name: 'Incoming webhook', ic: 'webhook', sub: 'External system fires a URL' },
  ] },
  // ── actions ───────────────────────────────────────────────────────
  { group: 'Messages', kind: 'action', cat: 'message', items: [
    // ⚠️ NOT DRAGGABLE — `palette: false` keeps it out of the left-hand list.
    // A notification step is a fixed, reviewed pairing of a moment and a piece
    // of platform copy (which the coach rewords in Settings → Communications),
    // not a blank box to fill in, so it ships read-only: it renders with its
    // real name and its own inspector line, and a coach adds one by using the
    // recipe that contains it. A full config UI would be a picker over an
    // allow-list of two.
    { type: 'action.notify_event', name: 'Send a notification', ic: 'mail', sub: 'Follows your Communications settings', palette: false },
    // Read-only and off the palette for a stronger reason than the one above:
    // this step takes a client's coaching away. It belongs to Payment rescue's
    // day 10 and nowhere else, and nobody should be able to drag it into an
    // onboarding sequence.
    { type: 'action.suspend_access', name: 'Suspend coaching access', ic: 'lock', sub: 'Day 10 of an unpaid payment', palette: false },
    { type: 'action.inapp', name: 'In-app message', ic: 'chat', sub: 'In their thread' },
    { type: 'action.push', name: 'Push notification', ic: 'bell', sub: 'Daytime only' },
    { type: 'action.email', name: 'Email', ic: 'mail', sub: 'Branded' },
    { type: 'action.sms', name: 'Text (SMS)', ic: 'chat', sub: 'Needs consent' },
  ] },
  { group: 'Logic', kind: 'action', cat: 'logic', items: [
    { type: 'logic.wait', name: 'Wait', ic: 'clock', sub: 'Hours or days' },
    { type: 'logic.ifthen', name: 'If / then', ic: 'branch', sub: 'Split the path' },
    { type: 'goal.exit', name: 'Exit goal', ic: 'target', sub: 'Leave when done' },
  ] },
  { group: 'Plans', kind: 'action', cat: 'crm', items: [
    { type: 'action.assign_workout', name: 'Assign a workout plan', ic: 'dumbbell', sub: 'Push a training plan' },
    { type: 'action.assign_nutrition', name: 'Assign a nutrition plan', ic: 'apple', sub: 'Push macros / meals' },
    { type: 'action.send_form', name: 'Send a check-in form', ic: 'doc', sub: 'Request a response' },
    { type: 'action.send_agreement', name: 'Send an agreement', ic: 'edit', sub: 'For e-signature' },
    { type: 'action.assign_habit', name: 'Assign a habit', ic: 'repeat', sub: 'Add a daily habit' },
    { type: 'action.enroll_flow', name: 'Start another automation', ic: 'branch', sub: 'Chain a flow' },
  ] },
  { group: 'CRM', kind: 'action', cat: 'crm', items: [
    { type: 'action.task', name: 'Create a task', ic: 'task', sub: 'On your list' },
    { type: 'action.tag', name: 'Add / remove tag', ic: 'tag', sub: 'Segment the client' },
    { type: 'action.note', name: 'Internal note', ic: 'note', sub: 'Timeline entry' },
  ] },
  { group: 'Integrations', kind: 'action', cat: 'integration', items: [
    { type: 'action.slack', name: 'Post to Slack', ic: 'slack', sub: 'Alert your team' },
    { type: 'action.sheets', name: 'Add row to Sheet', ic: 'sheet', sub: 'Google Sheets' },
    { type: 'action.notion', name: 'Create Notion item', ic: 'notion', sub: 'Docs / database' },
    { type: 'action.gcal', name: 'Create calendar event', ic: 'cal', sub: 'Google Calendar' },
    { type: 'action.webhook', name: 'Webhook', ic: 'webhook', sub: 'Zapier / Make / any' },
  ] },
];
const BYTYPE = {};
CATALOG.forEach((g) => g.items.forEach((i) => (BYTYPE[i.type] = { ...i, cat: g.cat })));

// The notifications an automation step may name, in the words the coach sees on
// their own Settings → Communications rows. Kept in step with
// ALLOWED_NOTIFY_EVENT_KEYS in _shared/automation-recipes.js.
const NOTIFY_EVENT_LABELS = {
  client_payment_reminder_to_client: 'Payment reminder',
  client_payment_final_reminder_to_client: 'Payment reminder, second notice',
};

// ── per-trigger settings ────────────────────────────────────────────
// Every trigger gets its OWN settings — not one generic box. `fields`
// render in the inspector (reusing .aue-field); `sum` builds the node's
// subtitle so the card reflects its settings live. Keys marked ✦ are
// honored right now by the execution-time gate (automation-engine
// triggerMatches, fed by the emitter's context); the rest save + show
// today and go live as each event source starts passing matching context.
const TRIG_FIELDS = {
  // training & nutrition
  'trigger.workout_logged': {
    fields: [{ key: 'scope', type: 'select', label: 'Which sessions', def: 'every',
      options: [['every', 'Every workout'], ['first', 'Their first ever'], ['weekly_first', 'First one each week']],
      help: 'When this fires for a client.' }],
    sum: (c) => ({ every: 'Any completed session', first: 'Their first workout ever', weekly_first: 'First workout each week' }[c.scope || 'every']),
  },
  'trigger.pr': {
    fields: [{ key: 'lift', type: 'text', label: 'Lift', placeholder: 'e.g. Back squat — blank = any lift',
      help: 'Only fire on a PR for this lift. Blank = any lift.' }],
    sum: (c) => (c.lift ? `New PR · ${c.lift}` : 'Any new personal record'),
  },
  'trigger.streak': {
    fields: [{ key: 'streakDays', type: 'number', label: 'Streak length (days)', def: 30, min: 2,
      help: 'Fires the day the streak reaches this length.' }],
    sum: (c) => `Reaches ${c.streakDays ?? 30} days`,
  },
  'trigger.nutrition_hit': {
    fields: [
      { key: 'daysInRow', type: 'number', label: 'Days on target in a row', def: 1, min: 1,
        help: '1 = any day they hit. Higher = a run of on-target days. “On target” uses the client’s own compliance setting.' },
    ],
    sum: (c) => ((c.daysInRow ?? 1) > 1 ? c.daysInRow + ' days on target' : 'On target'),
  },
  'trigger.nutrition_missed': {
    fields: [
      { key: 'daysOff', type: 'number', label: 'Days off target', def: 3, min: 1,
        help: 'Fires once they’ve been off target this many days running (client’s own compliance setting).' },
    ],
    sum: (c) => `${c.daysOff ?? 3} days off target`,
  },
  'trigger.nutrition_logged': {
    fields: [{ key: 'scope', type: 'select', label: 'Which logs', def: 'every',
      options: [['every', 'Every food log'], ['first_day', 'First log of the day']], help: 'How often this fires.' }],
    sum: (c) => (c.scope === 'first_day' ? 'First log each day' : 'Any food log'),
  },
  'trigger.milestone_unlocked': {
    fields: [{ key: 'milestone', type: 'text', label: 'Milestone', placeholder: 'e.g. 50 workouts — blank = any',
      help: 'Only this milestone (matches its name), or blank for any.' }],
    sum: (c) => (c.milestone ? `Unlocks · ${c.milestone}` : 'Any catalog milestone'),
  },
  'trigger.progress_photo': {
    fields: [{ key: 'scope', type: 'select', label: 'Which photos', def: 'every',
      options: [['every', 'Every photo'], ['first', 'Their first ever']], help: 'How often this fires.' }],
    sum: (c) => (c.scope === 'first' ? 'Their first photo' : 'New photo uploaded'),
  },
  'trigger.goal_weight': {
    fields: [],
    note: 'Fires when the client reaches their goal weight — the same milestone the app already tracks. No setup needed.',
    sum: () => 'Reaches their goal weight',
  },
  'trigger.checkin': {
    fields: [{ key: 'scope', type: 'select', label: 'Which check-ins', def: 'every',
      options: [['every', 'Every check-in'], ['first', 'Their first ever']], help: 'How often this fires.' }],
    sum: (c) => (c.scope === 'first' ? 'Their first check-in' : 'Weekly check-in lands'),
  },
  // business & lifecycle
  'trigger.new_client': {
    fields: [{ key: 'serviceType', type: 'select', label: 'Service type', def: 'any',
      options: [['any', 'Any client'], ['online', 'Online only'], ['in-person', 'In-person'], ['hybrid', 'Hybrid']],
      help: 'Limit to a service type, or any.' }],
    sum: (c) => (c.serviceType && c.serviceType !== 'any' ? `New ${c.serviceType} client` : 'Any new client'),
  },
  'trigger.payment_failed': {
    fields: [{ key: 'minAmount', type: 'number', label: 'Only over ($)', def: 0, min: 0,
      help: 'Skip small charges under this. 0 = every failed charge.' }],
    sum: (c) => ((c.minAmount ?? 0) > 0 ? `Failed charge over $${c.minAmount}` : 'Any recurring charge'),
  },
  'trigger.agreement_ending': {
    fields: [{ key: 'daysBefore', type: 'number', label: 'Days before it ends', def: 14, min: 1, max: 14,
      help: 'Fires this many days out. The renewal window is 14 days, so that’s the max.' }],
    sum: (c) => `${c.daysBefore ?? 14} days before renewal`,
  },
  'trigger.session_missed': {
    fields: [{ key: 'kind', type: 'select', label: 'Counts as missed', def: 'either',
      options: [['either', 'Any lost credit'], ['forfeited', 'Credit lost (late cancel / no-show)'], ['cancelled', 'On-time cancel (refunded)']],
      help: 'Which kind fires this — matching how the session was actually closed.' }],
    sum: (c) => ({ either: 'Any lost session', forfeited: 'Credit lost', cancelled: 'On-time cancel' }[c.kind || 'either']),
  },
  'trigger.client_message': {
    fields: [{ key: 'scope', type: 'select', label: 'Which replies', def: 'any',
      options: [['any', 'Any inbound message'], ['first', 'Their first ever reply']], help: 'How often this fires.' }],
    sum: (c) => (c.scope === 'first' ? 'Their first reply' : 'Any inbound message'),
  },
  'trigger.tag_added': {
    fields: [{ key: 'tag', type: 'text', label: 'Tag', placeholder: 'e.g. VIP', required: true,
      help: 'Fires when you add exactly this tag.' }],
    sum: (c) => (c.tag ? `You add · ${c.tag}` : 'Pick a tag to watch'),
  },
  'trigger.tenure': {
    fields: [
      { key: 'amount', type: 'number', label: 'How long', def: 1, min: 1 },
      { key: 'unit', type: 'select', label: 'Unit', def: 'years',
        options: [['days', 'Days'], ['weeks', 'Weeks'], ['months', 'Months'], ['years', 'Years']] },
    ],
    sum: (c) => { const a = c.amount ?? 1, u = c.unit || 'years'; return `At ${a} ${a === 1 ? u.replace(/s$/, '') : u}`; },
  },
  'trigger.birthday': {
    fields: [{ key: 'daysBefore', type: 'number', label: 'Days before', def: 0, min: 0,
      help: '0 = on the day. Higher fires early.' }],
    sum: (c) => ((c.daysBefore ?? 0) > 0 ? `${c.daysBefore} days before` : 'On their day'),
  },
  'trigger.agreement_signed': {
    fields: [{ key: 'match', type: 'text', label: 'Agreement name contains', placeholder: 'blank = any agreement',
      help: 'Fires when every required signer has signed (fully executed). Optional name filter.' }],
    sum: (c) => (c.match ? `Fully signed · ${c.match}` : 'Fully executed'),
  },
  'trigger.webhook_in': {
    fields: [
      { key: 'onlyField', type: 'text', label: 'Only when field', placeholder: 'e.g. plan (optional)' },
      { key: 'equalsValue', type: 'text', label: '…equals', placeholder: 'e.g. premium' },
    ],
    sum: (c) => (c.onlyField ? `When ${c.onlyField} = ${c.equalsValue || '…'}` : 'External system fires a URL'),
  },
};

// Render one trigger setting field (id namespaced `aef-t-<key>` so it
// never collides with the action field ids bound in renderInspector).
function trigFieldHtml(f, c) {
  const v = c[f.key];
  const id = 'aef-t-' + f.key;
  // Colorblind-safe required cue: a text marker on the label + in the help
  // (never color alone).
  const label = f.label + (f.required ? ' — required' : '');
  const help = (f.required ? 'Required. ' : '') + (f.help || '');
  if (f.type === 'select') {
    const opts = f.options.map(([val, lbl]) => `<option value="${esc(val)}" ${(v ?? f.def) === val ? 'selected' : ''}>${esc(lbl)}</option>`).join('');
    return F(label, `<select id="${id}" class="crm-select">${opts}</select>`, help);
  }
  if (f.type === 'number') {
    const attrs = `${f.min != null ? ` min="${f.min}"` : ''}${f.max != null ? ` max="${f.max}"` : ''}`;
    return F(label, `<input id="${id}" type="number"${attrs} value="${v ?? f.def ?? 0}">`, help);
  }
  return F(label, `<input id="${id}" value="${esc(v ?? '')}"${f.placeholder ? ` placeholder="${esc(f.placeholder)}"` : ''}>`, help);
}
function trigSummary(n) { const s = TRIG_FIELDS[n.type]; return s ? s.sum(n.config || {}) : (BYTYPE[n.type]?.sub || ''); }
// A trigger with an empty required field can't fire — the card shows a
// "finish setup" flag so a coach never leaves one half-configured.
function trigNeedsSetup(n) {
  const s = TRIG_FIELDS[n.type];
  if (!s) return false;
  const c = n.config || {};
  return s.fields.some((f) => f.required && !String(c[f.key] ?? '').trim());
}

// Habit colors — the exact 8-swatch palette the CRM's habit editor uses.
const HABIT_COLORS = [
  ['#6366f1', 'Indigo'], ['#10b981', 'Emerald'], ['#f59e0b', 'Amber'], ['#ef4444', 'Red'],
  ['#a855f7', 'Purple'], ['#06b6d4', 'Cyan'], ['#ec4899', 'Pink'], ['#f97316', 'Orange'],
];

// A dropdown backed by an async-loaded list ({id,name}[] | null while loading).
function pickerSelect(id, opts, current, emptyLabel) {
  let inner;
  if (opts == null) inner = '<option>Loading…</option>';
  else if (!opts.length) inner = `<option value="">${esc(emptyLabel)}</option>`;
  else inner = `<option value="" ${!current ? 'selected' : ''}>Choose…</option>`
    + opts.map((o) => `<option value="${esc(o.id)}" ${current === o.id ? 'selected' : ''}>${esc(o.name)}</option>`).join('');
  return `<select id="${id}" class="crm-select">${inner}</select>`;
}

// The coach's OTHER flows an enroll_flow step can start: custom flows + ON
// recipes that have a real row (flowId), excluding the flow being edited.
function enrollFlowOptions() {
  const raw = ed.pickers?.flows;
  if (raw == null) return null;
  const selfIds = [ed.flow.flowId, ed.flow.id, ed.flow.key].filter(Boolean);
  return [
    ...(raw.custom || []).map((x) => ({ id: x.id, name: x.name })),
    ...(raw.recipes || []).filter((x) => x.flowId && x.status === 'on').map((x) => ({ id: x.flowId, name: x.name })),
  ].filter((f) => f.id && !selfIds.includes(f.id));
}

// ── integration node inspector ──────────────────────────────────────
// Slack/Notion/Sheets/Calendar share a Connection row (live status from
// ed.pickers.integrations) + a per-provider target + the content field.
function integrationProvider(t) { return t === 'action.slack' ? 'slack' : t === 'action.notion' ? 'notion' : 'google'; }
function integrationFields(t, c) {
  const label = { 'action.slack': 'Slack', 'action.sheets': 'Google Sheets', 'action.notion': 'Notion', 'action.gcal': 'Google Calendar' }[t];
  const status = ed.pickers?.integrations;
  const conn = status ? status[integrationProvider(t)] : null;

  const connHtml = conn?.connected
    ? F('Connection', `<div style="font-size:12px;font-weight:600;color:var(--text);display:flex;align-items:center;gap:6px"><span style="color:#10b981">✓</span> Connected${conn.name ? ' · ' + esc(conn.name) : ''}</div>`, 'Manage connections in Settings → Integrations.')
    : F('Connection', `<button type="button" id="aef-connect" class="aue-connect">Connect ${esc(label)}<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7M9 7h8v8"/></svg></button>`,
        status ? 'Not connected yet — connect once in Settings → Integrations.' : 'Connect the account once in Settings → Integrations, then set the target here.');

  let target = '';
  // null list = still loading; [] once status is known (connected → channels,
  // not connected → the "Connect Slack first" empty label, never a stuck spinner).
  if (t === 'action.slack') target = F('Channel', pickerSelect('aef-slackchannel', status ? (conn?.connected ? (conn.channels || []) : []) : null, c.channel, conn?.connected ? 'No channels found' : 'Connect Slack first'), 'Where to post.');
  else if (t === 'action.notion') target = F('Database ID', `<input id="aef-notiondb" value="${esc(c.databaseId || '')}" placeholder="Notion database ID">`, 'The 32-char id from the database’s URL.');
  else if (t === 'action.sheets') target = F('Spreadsheet ID', `<input id="aef-sheetid" value="${esc(c.spreadsheetId || '')}" placeholder="from the sheet URL">`) + F('Tab name', `<input id="aef-sheettab" value="${esc(c.tab || '')}" placeholder="Sheet1">`, 'Appends a row: time · client · message.');
  else if (t === 'action.gcal') target = F('Calendar ID', `<input id="aef-gcalid" value="${esc(c.calendarId || '')}" placeholder="primary">`, 'Leave as primary for your main calendar. Creates an all-day event.');

  const content = t === 'action.gcal'
    ? F('Event title', `<input id="aef-title" value="${esc(c.title || '')}">`, 'Use {firstName}, {clientName} — filled per client.')
    : F('Message', `<textarea id="aef-body" rows="3">${esc(c.body || '')}</textarea>`) + tokenChips();
  return connHtml + target + content;
}

// ── node presentation ──────────────────────────────────────────────
function iconClass(type) {
  if (type.startsWith('trigger.')) return 'trig';
  if (type === 'logic.wait') return 'wait';
  if (type === 'logic.ifthen') return 'branch';
  if (type === 'goal.exit') return 'goal';
  if (type === 'system.note') return 'note';
  return type.split('.')[1] || 'email';
}
function catOf(type) { return type.split('.')[0]; }
function kindLabel(type) {
  if (type.startsWith('trigger.')) return 'Trigger';
  const map = {
    'logic.wait': 'Wait', 'logic.ifthen': 'If / then', 'goal.exit': 'Exit goal', 'system.note': 'Sent automatically',
    'action.email': 'Email', 'action.push': 'Push', 'action.inapp': 'In-app', 'action.sms': 'Text', 'action.task': 'Task',
    'action.notify_event': 'Notification',
    'action.suspend_access': 'Suspend access',
    'action.tag': 'Tag', 'action.note': 'Note', 'action.slack': 'Slack', 'action.sheets': 'Sheets',
    'action.notion': 'Notion', 'action.gcal': 'Calendar', 'action.webhook': 'Webhook',
    'action.assign_workout': 'Workout plan', 'action.assign_nutrition': 'Nutrition plan', 'action.send_form': 'Check-in form',
    'action.send_agreement': 'Agreement', 'action.assign_habit': 'Habit', 'action.enroll_flow': 'Enroll',
  };
  return map[type] || (BYTYPE[type]?.name) || type;
}
function waitLabel(n) { const d = n.config?.days, h = n.config?.hours; return `${d ? d + ' day' + (d === 1 ? '' : 's') : ''}${h ? (d ? ' ' : '') + h + 'h' : ''}`.trim() || 'a moment'; }
function nodeTitle(n) { return n.type === 'logic.wait' ? waitLabel(n) : (n.config?.label || BYTYPE[n.type]?.name || n.type.split('.')[1] || n.type); }
function nodeSub(n) {
  if (n.type === 'logic.wait') return '';
  // Triggers: the subtitle is computed from their settings, so the card
  // always reflects what the coach configured (not a static label).
  if (TRIG_FIELDS[n.type]) return trigSummary(n);
  return n.config?.detail || BYTYPE[n.type]?.sub || '';
}
function nodeIcon(type) { return svgIcon(IC[BYTYPE[type]?.ic] || IC.note); }

// ── editor state ────────────────────────────────────────────────────
let ed = null;

export function openEditor(view, flow, cb, handlers) {
  ed = {
    view, cb, flow, handlers,
    nodes: (flow.graph?.nodes || []).map((n) => ({ ...n, config: { ...(n.config || {}) } })),
    edges: (flow.graph?.edges || []).map((e) => ({ ...e })),
    selId: null, seq: 0, scale: 1, ox: 40, oy: 30,
    past: [], future: [], drag: null, conn: null, saveT: null, dirty: false,
    // populated async for send_form / enroll_flow / assign_workout /
    // assign_nutrition / send_agreement + integration connection status
    // (null = still loading)
    pickers: { forms: null, flows: null, programs: null, plans: null, agreements: null, integrations: null },
  };
  renderShell();
  paint();
  wireEvents();
  updUndo();
  preloadPickers();
}

// Load the option lists the action pickers need (check-in forms, other
// flows) once per editor session. Re-render the inspector if the coach is
// already sitting on the node that needs the freshly-loaded list.
function preloadPickers() {
  const e = ed;
  const refreshIf = (type) => { if (ed === e && ed.selId && ed.nodes.find((n) => n.id === ed.selId)?.type === type) renderInspector(); };
  // A failed load must still fall through to the empty state (not sit on
  // "Loading…"): set the list AND refreshIf on BOTH success and error, so an
  // open assign/send inspector re-renders either way.
  ed.cb.callFunction?.('checkin-forms-list', null, 'GET')
    .then((r) => { if (ed === e) { ed.pickers.forms = r?.forms || []; refreshIf('action.send_form'); } })
    .catch(() => { if (ed === e) { ed.pickers.forms = []; refreshIf('action.send_form'); } });
  ed.cb.callFunction?.('automation-flows-list', null, 'GET')
    .then((r) => { if (ed === e) { ed.pickers.flows = r || { custom: [], recipes: [] }; refreshIf('action.enroll_flow'); } })
    .catch(() => { if (ed === e) { ed.pickers.flows = { custom: [], recipes: [] }; refreshIf('action.enroll_flow'); } });
  // Plan pickers — all POST with an empty body (library scope: no clientId
  // → coach-level programs; is_template plans; all agreement templates).
  const toOpts = (arr, nameKey = 'name') => (arr || []).map((x) => ({ id: x.id, name: x[nameKey] || 'Untitled' }));
  ed.cb.callFunction?.('workout-program-list', {}, 'POST')
    .then((r) => { if (ed === e) { ed.pickers.programs = toOpts(r?.programs); refreshIf('action.assign_workout'); } })
    .catch(() => { if (ed === e) { ed.pickers.programs = []; refreshIf('action.assign_workout'); } });
  ed.cb.callFunction?.('nutrition-plan-list', {}, 'POST')
    .then((r) => { if (ed === e) { ed.pickers.plans = toOpts(r?.plans); refreshIf('action.assign_nutrition'); } })
    .catch(() => { if (ed === e) { ed.pickers.plans = []; refreshIf('action.assign_nutrition'); } });
  ed.cb.callFunction?.('agreement-template-list', {}, 'POST')
    .then((r) => { if (ed === e) { ed.pickers.agreements = toOpts(r?.templates); refreshIf('action.send_agreement'); } })
    .catch(() => { if (ed === e) { ed.pickers.agreements = []; refreshIf('action.send_agreement'); } });
  // Integration connection status (Slack/Notion/Google) — feeds the Connect
  // state + the Slack channel picker. Refresh all 4 integration node types.
  const refreshIntegrations = () => ['action.slack', 'action.notion', 'action.sheets', 'action.gcal'].forEach(refreshIf);
  ed.cb.callFunction?.('integration-status', null, 'GET')
    .then((r) => { if (ed === e) { ed.pickers.integrations = r || {}; refreshIntegrations(); } })
    .catch(() => { if (ed === e) { ed.pickers.integrations = {}; refreshIntegrations(); } });
}

function newId() { let id; do { id = 'n' + (ed.seq++); } while (ed.nodes.some((n) => n.id === id)); return id; }
function nodeEl(id) { return document.getElementById('aue-' + id); }
const $ = (id) => document.getElementById(id);

// ── shell ───────────────────────────────────────────────────────────
function renderShell() {
  ed.view.style.padding = '0'; // editor runs edge-to-edge; gallery restores it
  const f = ed.flow, on = f.status === 'on';
  let _lastKind = null;
  const palette = CATALOG.map((g) => {
    // `palette: false` — a step that exists on the canvas and in the registry
    // but is not something a coach drags in (see action.notify_event).
    const items = g.items.filter((it) => it.palette !== false);
    if (!items.length) return '';
    // Divider between the trigger half and the action half of the palette.
    const section = g.kind !== _lastKind
      ? `<div class="aue-pal-section">${g.kind === 'trigger' ? 'When it happens' : 'Then do this'}</div>`
      : '';
    _lastKind = g.kind;
    return `${section}
    <div class="aue-pal-h">${esc(g.group)}</div>
    ${items.map((it) => `<div class="aue-pal-item t-${g.cat}" draggable="true" data-type="${it.type}">
        <span class="aue-pal-ic">${svgIcon(IC[it.ic])}</span><span>${esc(it.name)}</span></div>`).join('')}`;
  }).join('');

  ed.view.innerHTML = `
    <div class="aue-shell">
      <div class="aue-top">
        <button class="au-back" id="aue-back">← Automations</button>
        <input class="aue-name" id="aue-name" value="${esc(f.name)}" spellcheck="false" placeholder="Untitled automation">
        <span class="au-pill ${on ? 'on' : 'off'}">${on ? 'ON' : 'OFF'}</span>
        <span class="aue-grow"></span>
        <span class="aue-save" id="aue-save">Saved</span>
        <span class="aue-div"></span>
        <button class="aue-icon" id="aue-undo" title="Undo (⌘Z)" disabled>${svgIcon(IC.undo)}</button>
        <button class="aue-icon" id="aue-redo" title="Redo (⌘⇧Z)" disabled>${svgIcon(IC.redo)}</button>
        <span class="aue-div"></span>
        <button class="btn-secondary btn-sm" id="aue-runs">Runs log</button>
        <button class="btn-secondary btn-sm" id="aue-toggle">${on ? 'Turn off' : 'Turn on'}</button>
      </div>
      <div class="aue-body">
        <div class="aue-palette" id="aue-palette">${palette}
          <div class="aue-pal-hint">Drag any step onto the canvas. Drag the dots to connect. Click a step to edit.</div>
        </div>
        <div class="aue-canvas" id="aue-canvas">
          <div class="au-world" id="aue-world">
            <svg class="au-wires" id="aue-wires"><defs>
              <marker id="aue-arr" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto"><path d="M0 .6 L7.4 4 L0 7.4 z" fill="var(--au-wire)"/></marker>
            </defs></svg>
          </div>
          <div class="au-zoom"><button data-z="out">−</button><div class="au-zpct" id="aue-zpct">100%</div><button data-z="in">+</button></div>
          <div class="au-cv-hint">Drag to pan · scroll to zoom</div>
        </div>
        <div class="aue-inspector empty" id="aue-inspector">Select a step to edit it — or drag a new one from the left.</div>
      </div>
    </div>`;
  applyView();
}

// ── paint (nodes + wires) ───────────────────────────────────────────
function paint() {
  const world = $('aue-world');
  world.querySelectorAll('.au-node').forEach((n) => n.remove());
  world.querySelectorAll('.au-wire-lbl').forEach((n) => n.remove());
  for (const n of ed.nodes) {
    const el = document.createElement('div');
    el.className = `au-node au-node--${catOf(n.type)} ${n.type === 'logic.wait' ? 'au-node--wait' : ''} ${n.type === 'goal.exit' ? 'au-node--goalcard' : ''} ${n.id === ed.selId ? 'aue-sel' : ''}`;
    el.id = 'aue-' + n.id;
    el.style.left = (n.x || 0) + 'px';
    el.style.top = (n.y || 0) + 'px';
    el.dataset.id = n.id;
    el.innerHTML = `
      <div class="au-nh"><span class="au-nic au-nic--${iconClass(n.type)}">${nodeIcon(n.type)}</span><span class="au-nkind">${esc(kindLabel(n.type))}</span></div>
      <div class="au-ntt">${esc(nodeTitle(n))}</div>
      ${nodeSub(n) ? `<div class="au-ncfg">${esc(nodeSub(n))}</div>` : ''}
      ${trigNeedsSetup(n) ? '<div class="au-nsetup">Finish setup</div>' : ''}
      ${n.type.startsWith('trigger.') ? '' : '<span class="au-handle in" data-h="in"></span>'}
      ${outHandles(n)}`;
    world.appendChild(el);
  }
  // A flow closed before the next frame (Back, or a switch to another Builder
  // tab) has no editor left to draw from (2026-10-12).
  requestAnimationFrame(() => { if (!ed) return; drawWires(); sizeWorld(); });
}
/**
 * Does this step leave by a "yes" exit and a "no" exit?
 *
 * ⚠️ IT IS NOT ONLY `logic.ifthen` ANY MORE, AND THE CANVAS SAID IT WAS.
 * `action.suspend_access` forks too: its 'yes' exit tells the client once, its
 * 'no' exit skips straight to the coach's call task, because a client the
 * platform deliberately passed over (an override, a payment arrangement) must
 * never be emailed to say their access has gone. drawWires already places
 * labelled wires at 34% and 66% of a node's height, so the two wires left the
 * suspension step from a handle that was not drawn there.
 *
 * ⚠️ ASKED OF THE EDGES ON THE CANVAS, NOT OF A LIST OF TYPES. The fork is a
 * fact about the graph in front of the coach, and reading it from the graph
 * means the next action that forks needs no second place to be registered.
 *
 * @param {{id: string, type: string}} n
 * @param {{from: string, to: string, label?: string}[]} edges
 * @returns {boolean}
 */
export function _forksYesNo(n, edges) {
  if (n?.type === 'logic.ifthen') return true;
  return (edges || []).some((e) => e && e.from === n?.id && (e.label === 'yes' || e.label === 'no'));
}

function outHandles(n) {
  if (n.type === 'goal.exit') return '';
  if (_forksYesNo(n, ed?.edges)) return '<span class="au-handle out yes" data-h="out" data-branch="yes"></span><span class="au-hlbl yes">yes</span><span class="au-handle out no" data-h="out" data-branch="no"></span><span class="au-hlbl no">no</span>';
  return '<span class="au-handle out" data-h="out"></span>';
}
function drawWires() {
  const svg = $('aue-wires');
  svg.querySelectorAll('path.au-wire').forEach((p) => p.remove());
  $('aue-world').querySelectorAll('.au-wire-lbl').forEach((n) => n.remove());
  for (const e of ed.edges) {
    const a = nodeEl(e.from), b = nodeEl(e.to); if (!a || !b) continue;
    const frac = e.label === 'yes' ? 0.34 : e.label === 'no' ? 0.66 : 0.5;
    const x1 = a.offsetLeft + a.offsetWidth, y1 = a.offsetTop + a.offsetHeight * frac;
    const x2 = b.offsetLeft, y2 = b.offsetTop + b.offsetHeight / 2;
    const dx = Math.max(40, (x2 - x1) / 2);
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('class', 'au-wire'); p.setAttribute('marker-end', 'url(#aue-arr)');
    p.setAttribute('d', `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`);
    svg.appendChild(p);
    if (e.label) { const l = document.createElement('div'); l.className = 'au-wire-lbl'; l.textContent = e.label.toUpperCase(); l.style.left = (x1 + x2) / 2 + 'px'; l.style.top = (y1 + y2) / 2 + 'px'; $('aue-world').appendChild(l); }
  }
}
function sizeWorld() {
  const world = $('aue-world'); if (!world) return;
  let maxX = 1400, maxY = 800;
  world.querySelectorAll('.au-node').forEach((n) => { maxX = Math.max(maxX, n.offsetLeft + n.offsetWidth + 300); maxY = Math.max(maxY, n.offsetTop + n.offsetHeight + 300); });
  world.style.width = maxX + 'px'; world.style.height = maxY + 'px';
  const svg = $('aue-wires'); if (svg) { svg.setAttribute('width', maxX); svg.setAttribute('height', maxY); }
}

// ── events ──────────────────────────────────────────────────────────
function wireEvents() {
  const canvas = $('aue-canvas'), world = $('aue-world');
  $('aue-back').onclick = () => ed.handlers.onBack();
  $('aue-runs').onclick = () => ed.handlers.onRuns();
  $('aue-toggle').onclick = () => ed.handlers.onToggle();
  $('aue-undo').onclick = undo; $('aue-redo').onclick = redo;
  $('aue-name').addEventListener('input', () => { ed.flow.name = $('aue-name').value; touch(); });

  // palette drag
  $('aue-palette').querySelectorAll('.aue-pal-item').forEach((it) => {
    it.addEventListener('dragstart', (e) => e.dataTransfer.setData('type', it.dataset.type));
  });
  canvas.addEventListener('dragover', (e) => { e.preventDefault(); canvas.classList.add('dropok'); });
  canvas.addEventListener('dragleave', () => canvas.classList.remove('dropok'));
  canvas.addEventListener('drop', onDrop);

  // node move / connect
  world.addEventListener('mousedown', onWorldDown);
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);

  // pan on empty canvas
  canvas.addEventListener('mousedown', (e) => {
    if (e.target === canvas || e.target === world || e.target.classList.contains('au-cv-hint')) {
      ed.pan = { x: e.clientX, y: e.clientY, ox: ed.ox, oy: ed.oy }; select(null);
    }
  });
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault(); const prev = ed.scale;
    ed.scale = Math.min(1.6, Math.max(0.4, ed.scale - e.deltaY * 0.0012));
    const r = canvas.getBoundingClientRect(), cx = e.clientX - r.left, cy = e.clientY - r.top;
    ed.ox = cx - ((cx - ed.ox) * ed.scale) / prev; ed.oy = cy - ((cy - ed.oy) * ed.scale) / prev;
    applyView();
  }, { passive: false });
  canvas.querySelector('[data-z="in"]').onclick = () => { ed.scale = Math.min(1.6, ed.scale + 0.12); applyView(); };
  canvas.querySelector('[data-z="out"]').onclick = () => { ed.scale = Math.max(0.4, ed.scale - 0.12); applyView(); };

  document.addEventListener('keydown', onKey);
  // These three listeners live on window/document, so they outlive the editor's
  // own DOM. closeEditor() nulls `ed`, and onMove/onUp/onKey dereference it — so
  // teardown MUST detach all three or they keep firing against a null `ed`.
  ed._off = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    document.removeEventListener('keydown', onKey);
  };
}

function onDrop(e) {
  e.preventDefault(); $('aue-canvas').classList.remove('dropok');
  const type = e.dataTransfer.getData('type'); if (!type || !BYTYPE[type]) return;
  commit();
  const canvas = $('aue-canvas'), r = canvas.getBoundingClientRect();
  const wx = (e.clientX - r.left - ed.ox) / ed.scale, wy = (e.clientY - r.top - ed.oy) / ed.scale;
  const insertable = !type.startsWith('trigger.') && type !== 'goal.exit';
  const hit = insertable ? edgeAtPoint(wx, wy) : null;
  const id = addNode(type, Math.round(wx - 105), Math.round(wy - 36));
  if (hit) { const { from: A, to: B, label: L } = hit; ed.edges = ed.edges.filter((x) => x !== hit); ed.edges.push({ from: A, to: id, ...(L ? { label: L } : {}) }, { from: id, to: B }); }
  paint(); select(id); touch();
}
function addNode(type, x, y) {
  const id = newId();
  let seed = { 'logic.wait': { days: 1 } }[type] || {};
  // Seed a trigger with its default settings so the saved graph is
  // concrete (the card + the execution-time gate read real values).
  if (TRIG_FIELDS[type]) { seed = {}; for (const f of TRIG_FIELDS[type].fields) if (f.def !== undefined) seed[f.key] = f.def; }
  ed.nodes.push({ id, type, x, y, config: { ...seed } });
  return id;
}

function onWorldDown(e) {
  const handle = e.target.closest('.au-handle'), nd = e.target.closest('.au-node');
  if (handle && handle.dataset.h === 'out') {
    e.stopPropagation();
    ed.conn = { from: nd.dataset.id, branch: handle.dataset.branch || null };
    const tmp = document.createElementNS('http://www.w3.org/2000/svg', 'path'); tmp.setAttribute('class', 'au-wire tmp'); tmp.id = 'aue-tmpwire'; $('aue-wires').appendChild(tmp);
    return;
  }
  if (nd) {
    const id = nd.dataset.id; select(id);
    const n = ed.nodes.find((x) => x.id === id);
    ed.drag = { id, dx: e.clientX / ed.scale - n.x, dy: e.clientY / ed.scale - n.y, snap: gclone(), moved: false };
  }
}
function onMove(e) {
  if (ed.drag) {
    const n = ed.nodes.find((x) => x.id === ed.drag.id); if (!n) return;
    if (!ed.drag.moved) { ed.drag.moved = true; ed.past.push(ed.drag.snap); ed.future = []; updUndo(); }
    n.x = Math.round(e.clientX / ed.scale - ed.drag.dx); n.y = Math.round(e.clientY / ed.scale - ed.drag.dy);
    const el = nodeEl(n.id); el.style.left = n.x + 'px'; el.style.top = n.y + 'px'; drawWires();
  }
  if (ed.conn) {
    const a = nodeEl(ed.conn.from), r = $('aue-world').getBoundingClientRect();
    if (!a) return;
    const x1 = a.offsetLeft + a.offsetWidth, y1 = a.offsetTop + a.offsetHeight / 2;
    const x2 = (e.clientX - r.left) / ed.scale, y2 = (e.clientY - r.top) / ed.scale;
    $('aue-tmpwire')?.setAttribute('d', `M ${x1} ${y1} C ${x1 + 60} ${y1}, ${x2 - 60} ${y2}, ${x2} ${y2}`);
  }
  if (ed.pan) { ed.ox = ed.pan.ox + (e.clientX - ed.pan.x); ed.oy = ed.pan.oy + (e.clientY - ed.pan.y); applyView(); }
}
function onUp(e) {
  if (ed.conn) {
    const t = e.target.closest('.au-node'); $('aue-tmpwire')?.remove();
    if (t) {
      const to = t.dataset.id;
      if (to !== ed.conn.from) {
        commit();
        ed.edges = ed.edges.filter((x) => !(x.from === ed.conn.from && (x.label || null) === (ed.conn.branch || null)));
        if (!ed.edges.some((x) => x.from === ed.conn.from && x.to === to && (x.label || null) === (ed.conn.branch || null)))
          ed.edges.push({ from: ed.conn.from, to, ...(ed.conn.branch ? { label: ed.conn.branch } : {}) });
        paint(); touch();
      }
    }
    ed.conn = null;
  }
  if (ed.drag && ed.drag.moved) touch();
  ed.drag = null; ed.pan = null;
}
function onKey(e) {
  // ⚠️ AUTOMATIONS IS A BUILDER TAB (2026-10-12). Another tab, or another page,
  // hides an open flow without closing it; a key pressed there is not for it
  // (Backspace on the Workout tab must not delete the selected step).
  const host = document.getElementById('view-automations');
  if (host && host.closest('.builder-tab-panel:not(.active), .view.hidden')) return;
  const typing = /INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName);
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); return; }
  if ((e.key === 'Delete' || e.key === 'Backspace') && ed.selId && !typing) { deleteSel(); }
}
function deleteSel() {
  if (!ed.selId) return;
  commit();
  ed.nodes = ed.nodes.filter((x) => x.id !== ed.selId);
  ed.edges = ed.edges.filter((x) => x.from !== ed.selId && x.to !== ed.selId);
  ed.selId = null; paint(); renderInspector(); touch();
}
function applyView() { const w = $('aue-world'); if (!w) return; w.style.transform = `translate(${ed.ox}px,${ed.oy}px) scale(${ed.scale})`; w.style.transformOrigin = '0 0'; const p = $('aue-zpct'); if (p) p.textContent = Math.round(ed.scale * 100) + '%'; }

// ── inspector ───────────────────────────────────────────────────────
function select(id) { ed.selId = id; paint(); renderInspector(); }
function renderInspector() {
  const insp = $('aue-inspector');
  if (!ed.selId) { insp.className = 'aue-inspector empty'; insp.textContent = 'Select a step to edit it — or drag a new one from the left.'; return; }
  const n = ed.nodes.find((x) => x.id === ed.selId); if (!n) return;
  insp.className = 'aue-inspector';
  let fields = '';
  const t = n.type, c = n.config || {};
  if (t === 'logic.wait') fields = F('Wait', `<input id="aef-days" type="number" min="0" value="${c.days || 0}"> days`, 'How long before the next step runs.');
  else if (t === 'action.email') fields = F('Subject', `<input id="aef-subject" value="${esc(c.subject || '')}">`) + F('Body', `<textarea id="aef-body" rows="5">${esc(c.body || '')}</textarea>`, 'Use {firstName}, {coachName} — filled per client.') + tokenChips();
  else if (t === 'action.inapp' || t === 'action.push') fields = F('Message', `<textarea id="aef-body" rows="4">${esc(c.body || '')}</textarea>`, 'What the client sees.') + tokenChips();
  else if (t === 'action.sms') fields = F('Text', `<textarea id="aef-body" rows="3">${esc(c.body || '')}</textarea>`, 'Only sends if the client gave texting consent.');
  // Read-only on purpose: the wording lives in Settings → Communications, where
  // the coach edits it once for every place the platform sends it, and the
  // channels are the switches on that same row. Repeating either here would be
  // two places to change one message.
  else if (t === 'action.notify_event') fields = F(
    'Notification',
    `<div class="aue-ro">${esc(NOTIFY_EVENT_LABELS[c.eventKey] || c.eventKey || 'Not set')}</div>`,
    'Sent in your business name from the platform, not from your own number, and it follows your switches in Settings → Communications. Edit the wording there.',
  );
  // Read-only, and there is nothing to configure on purpose: what this step
  // does, when it stops itself, and what it leaves alone are product decisions
  // rather than settings. The one control a coach has over it is the per-client
  // override on the client's own profile.
  else if (t === 'action.suspend_access') fields = F(
    'What this does',
    '<div class="aue-ro">Suspends this client\'s coaching access</div>',
    'Checks what is still owed first. Their upcoming sessions are cancelled and the credits are kept, '
    + 'on the dates they already had. They keep logging in, messaging you, and updating their card. '
    + 'It lifts by itself the moment nothing is owed. Skipped for a client you have set to keep access, '
    + 'or one with a payment arrangement on record.',
  );
  else if (t === 'logic.ifthen') fields = F('Condition', `<select id="aef-cond" class="crm-select">${['sms_available', 'client_returned'].map((o) => `<option value="${o}" ${c.condition === o ? 'selected' : ''}>${condLabel(o)}</option>`).join('')}</select>`, 'The flow splits into a “yes” path and a “no” path.');
  else if (t === 'action.task') fields = F('Task title', `<input id="aef-title" value="${esc(c.title || '')}">`) + F('Notes', `<textarea id="aef-body" rows="3">${esc(c.notes || '')}</textarea>`);
  else if (t === 'action.tag') fields = F('Tag', `<input id="aef-tag" value="${esc(c.tag || '')}">`) + F('Action', `<select id="aef-op" class="crm-select"><option value="add" ${c.op !== 'remove' ? 'selected' : ''}>Add</option><option value="remove" ${c.op === 'remove' ? 'selected' : ''}>Remove</option></select>`);
  else if (t === 'action.note') fields = F('Note', `<textarea id="aef-body" rows="4">${esc(c.body || '')}</textarea>`, 'Adds a note to the client’s timeline. Use {firstName}, {coachName}.');
  else if (t === 'action.assign_habit') fields =
    F('Habit name', `<input id="aef-hname" value="${esc(c.name || '')}" placeholder="e.g. 10k steps">`, 'Added as a daily habit. Use {firstName} if you like.')
    + F('Color', `<select id="aef-hcolor" class="crm-select">${HABIT_COLORS.map(([hex, label]) => `<option value="${hex}" ${(c.color || HABIT_COLORS[0][0]) === hex ? 'selected' : ''}>${esc(label)}</option>`).join('')}</select>`);
  else if (t === 'action.send_form') fields = F('Check-in form', pickerSelect('aef-formid', ed.pickers?.forms, c.formId, 'No check-in forms yet'), 'Sets this as the client’s check-in form.');
  else if (t === 'action.assign_workout') fields = F('Workout program', pickerSelect('aef-programid', ed.pickers?.programs, c.programId, 'No workout programs yet'), 'Skips if the client already has this program.')
    + F('Training days', weekdayPicker(c.trainingDays), 'Which days of the week this client trains. The program’s workouts land on these days in order — pick three days for a three-day split. Leave them all off to use the program’s own schedule.');
  else if (t === 'action.assign_nutrition') fields = F('Nutrition plan', pickerSelect('aef-planid', ed.pickers?.plans, c.templatePlanId, 'No nutrition plans yet'), 'Assigns this plan and activates it. Any current plan is archived.');
  else if (t === 'action.send_agreement') fields = F('Agreement template', pickerSelect('aef-agreementid', ed.pickers?.agreements, c.templateId, 'No agreement templates yet'), 'Emails the agreement to the client for e-signature. Needs an email on file.');
  else if (t === 'action.enroll_flow') fields = F('Automation to start', pickerSelect('aef-enrollid', enrollFlowOptions(), c.flowId, 'No other automations are on'), 'Enrolls the client into another of your automations.');
  else if (t === 'action.webhook') fields = F('Webhook URL', `<input id="aef-url" value="${esc(c.url || '')}">`, 'https only. Connects to Zapier / Make / anything.');
  else if (['action.slack', 'action.sheets', 'action.notion', 'action.gcal'].includes(t)) {
    fields = integrationFields(t, c);
  }
  else if (t === 'goal.exit') fields = F('Leave the flow when', `<input id="aef-label" value="${esc(c.label || '')}">`, 'The client exits instantly when this happens.');
  else if (TRIG_FIELDS[t]) {
    const spec = TRIG_FIELDS[t];
    fields = spec.fields.map((f) => trigFieldHtml(f, c)).join('');
    // A trigger with no settings (e.g. goal_weight) shows a short note.
    if (!fields && spec.note) fields = `<div class="aue-help" style="margin-top:2px">${esc(spec.note)}</div>`;
  }
  else fields = F('Label', `<input id="aef-label" value="${esc(c.label || '')}">`);

  insp.innerHTML = `<div class="aue-insp-hd"><span class="au-nic au-nic--${iconClass(t)}">${nodeIcon(t)}</span><span class="aue-insp-kind">${esc(kindLabel(t))}</span></div>
    <h3 class="aue-insp-title">${esc(nodeTitle(n))}</h3>${fields}
    <button class="aue-del" id="aef-del">Remove this step</button>`;
  bindField('aef-days', 'days', true); bindField('aef-subject', 'subject'); bindField('aef-body', t === 'action.task' ? 'notes' : 'body');
  bindField('aef-title', 'title'); bindField('aef-tag', 'tag');
  bindField('aef-hname', 'name'); bindSelect('aef-hcolor', 'color');
  bindSelect('aef-formid', 'formId'); bindSelect('aef-enrollid', 'flowId');
  bindSelect('aef-programid', 'programId'); bindSelect('aef-planid', 'templatePlanId'); bindSelect('aef-agreementid', 'templateId');
  bindWeekdays();
  // integration targets
  bindSelect('aef-slackchannel', 'channel'); bindField('aef-notiondb', 'databaseId');
  bindField('aef-sheetid', 'spreadsheetId'); bindField('aef-sheettab', 'tab'); bindField('aef-gcalid', 'calendarId');
  bindField('aef-url', 'url'); bindField('aef-label', 'label'); bindField('aef-detail', 'detail');
  bindSelect('aef-cond', 'condition'); bindSelect('aef-op', 'op');
  // Per-trigger settings — bind each field to its config key (selects on
  // change, text/number on input; numbers coerce via bindField's num flag).
  if (TRIG_FIELDS[t]) TRIG_FIELDS[t].fields.forEach((f) => {
    const id = 'aef-t-' + f.key;
    if (f.type === 'select') bindSelect(id, f.key); else bindField(id, f.key, f.type === 'number');
  });
  $('aef-del').onclick = deleteSel;
  // Integration "Connect …" button → hand off to the host (automations.js)
  // which tears the editor down and deep-links to Settings → Integrations.
  $('aef-connect')?.addEventListener('click', () => ed.handlers.onConnectIntegration?.());
  insp.querySelectorAll('.aue-chip').forEach((chip) => chip.addEventListener('click', () => insertToken(chip.textContent)));
  // Swap the native <select>s for the CRM's custom dropdown (native option
  // lists are OS-rendered and can't be themed). Dispatches a native change,
  // so the bindSelect listeners above still fire.
  upgradeSelects(insp);
}
function condLabel(o) { return { sms_available: 'Texting set up?', client_returned: 'Did the client come back?', agreement_renewed: 'Have they renewed?', testimonial_left: 'Left a testimonial?' }[o] || o; }
function tokenChips() { return `<div class="aue-chips">${['{firstName}', '{coachName}', '{clientName}', '{cardUpdateLink}'].map((x) => `<span class="aue-chip">${x}</span>`).join('')}</div>`; }
function insertToken(tok) { const ta = $('aef-body'); if (!ta) return; const n = ed.nodes.find((x) => x.id === ed.selId); const key = n.type === 'action.task' ? 'notes' : 'body'; ta.value = (ta.value || '') + tok; n.config[key] = ta.value; ta.focus(); paint(); touch(); }
function bindField(id, key, num) {
  const el = $(id); if (!el) return;
  el.addEventListener('focus', () => { el._snap = gclone(); el._edd = false; });
  el.addEventListener('input', () => {
    if (!el._edd) { ed.past.push(el._snap || gclone()); ed.future = []; el._edd = true; updUndo(); }
    const n = ed.nodes.find((x) => x.id === ed.selId); n.config[key] = num ? +el.value : el.value; paint(); touch();
  });
}
function bindSelect(id, key) { const el = $(id); if (!el) return; el.addEventListener('change', () => { commit(); const n = ed.nodes.find((x) => x.id === ed.selId); n.config[key] = el.value; paint(); touch(); }); }

// Seven day toggles. An automation fires with nobody there to ask, so the
// client's training weekdays have to be chosen here, at build time.
const WEEKDAYS = [['mon', 'M'], ['tue', 'T'], ['wed', 'W'], ['thu', 'T'], ['fri', 'F'], ['sat', 'S'], ['sun', 'S']];
function weekdayPicker(selected) {
  const on = new Set(Array.isArray(selected) ? selected : []);
  return `<div class="aue-weekdays" id="aef-weekdays">${WEEKDAYS.map(([key, initial]) => `
    <button type="button" class="aue-wd${on.has(key) ? ' on' : ''}" data-weekday="${key}"
            aria-pressed="${on.has(key) ? 'true' : 'false'}" aria-label="${key}">${initial}</button>`).join('')}</div>`;
}
function bindWeekdays() {
  const wrap = $('aef-weekdays'); if (!wrap) return;
  wrap.addEventListener('click', (e) => {
    const btn = e.target.closest('.aue-wd'); if (!btn) return;
    commit();
    const n = ed.nodes.find((x) => x.id === ed.selId); if (!n) return;
    const current = new Set(Array.isArray(n.config.trainingDays) ? n.config.trainingDays : []);
    const key = btn.dataset.weekday;
    if (current.has(key)) current.delete(key); else current.add(key);
    // Store in calendar order so the saved config reads the way it looks.
    n.config.trainingDays = WEEKDAYS.map(([k]) => k).filter((k) => current.has(k));
    btn.classList.toggle('on', current.has(key));
    btn.setAttribute('aria-pressed', current.has(key) ? 'true' : 'false');
    paint(); touch();
  });
}

// ── undo / redo + auto-save ─────────────────────────────────────────
const gclone = () => ({ nodes: JSON.parse(JSON.stringify(ed.nodes)), edges: JSON.parse(JSON.stringify(ed.edges)), sel: ed.selId });
function commit() { ed.past.push(gclone()); if (ed.past.length > 60) ed.past.shift(); ed.future = []; updUndo(); }
function grestore(s) { ed.nodes = s.nodes; ed.edges = s.edges; ed.selId = s.sel; paint(); renderInspector(); updUndo(); touch(); }
function undo() { if (!ed.past.length) return; ed.future.push(gclone()); grestore(ed.past.pop()); }
function redo() { if (!ed.future.length) return; ed.past.push(gclone()); grestore(ed.future.pop()); }
function updUndo() { const u = $('aue-undo'), r = $('aue-redo'); if (u) u.disabled = !ed.past.length; if (r) r.disabled = !ed.future.length; }

function setStatus(s) { const el = $('aue-save'); if (!el) return; el.className = 'aue-save' + (s === 'saving' ? ' saving' : s === 'error' ? ' err' : ''); el.textContent = s === 'saving' ? 'Saving…' : s === 'error' ? 'Save failed' : 'Saved'; }
function touch() {
  setStatus('saving'); clearTimeout(ed.saveT);
  ed.saveT = setTimeout(save, 700);
}
async function save() {
  if (!ed) return; // torn down (e.g. navigated to Settings) before the debounce fired
  const graph = { nodes: ed.nodes, edges: ed.edges };
  const f = ed.flow;
  const name = (f.name || '').trim().slice(0, 80) || 'Untitled automation';
  const isNew = !f.recipeKey && !(f.flowId || f.key || f.id);
  const payload = f.recipeKey ? { recipeKey: f.recipeKey, graph, name }
    : isNew ? { graph, name }                                   // build-your-own → create
    : { flowId: f.flowId || f.key || f.id, graph, name };
  try {
    const res = await ed.cb.callFunction('automation-flows-update', payload, 'POST');
    // First save of a new / recipe flow returns the row id → target it directly after.
    if (res?.flow?.id && !f.flowId) ed.flow.flowId = res.flow.id;
    setStatus('saved');
  } catch (e) { setStatus('error'); }
}

// ── geometry helpers ────────────────────────────────────────────────
function edgeAtPoint(px, py) {
  let best = null, bd = 36;
  for (const e of ed.edges) {
    const a = nodeEl(e.from), b = nodeEl(e.to); if (!a || !b) continue;
    const x1 = a.offsetLeft + a.offsetWidth, y1 = a.offsetTop + a.offsetHeight / 2;
    const x2 = b.offsetLeft, y2 = b.offsetTop + b.offsetHeight / 2;
    const d = segDist(px, py, x1, y1, x2, y2);
    if (d < bd) { bd = d; best = e; }
  }
  return best;
}
function segDist(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1, L2 = dx * dx + dy * dy || 1;
  let t = ((px - x1) * dx + (py - y1) * dy) / L2; t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

export function closeEditor() { if (ed?._off) ed._off(); if (ed?.saveT) clearTimeout(ed.saveT); ed = null; }
